import React from 'react';
import moment from 'moment';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { appConfigStore } from 'Models/AppConfigStore';
import { modalWithCustomInput } from 'Views/Components/Modal/CustomModal';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import alertToast from 'Util/ToastifyUtils';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import {
	convertVoucherToManualRefund,
	toggleVoucherDisabled,
	updateGiftVoucher,
} from 'Services/Api/_HumanWritten/GiftCertificateService';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { TextArea } from 'Views/Components/TextArea/TextArea';
import GiftVoucherModalContent, { GiftVoucherState } from './GiftVoucherModalContent';

export type VoucherStatus = 'VALID' | 'EXPIRED' | 'USED' | 'USEDEXPIRED';

export interface GiftVoucherUsageDto {
	humanReadableId: string;
	amount: number;
	date: string;
}

export interface FetchedGiftVouchersDto {
	giftVouchers: GiftVoucherDto[];
	count: number;
}

export interface GiftVoucherDto {
	id: string;
	userId: string;
	code: string;
	status: VoucherStatus;
	remainingValue: number;
	initialValue: number;
	expiryDate: Date;
	giftCertificateUsages: GiftVoucherUsageDto[];
	price?: number;
	transactionId?: string;
	invoiced?: boolean;
	recipientEmail?: string;
	message?: string;
	createdDate?: string;
	disabled?: boolean;
	/**
	 * Server-computed: whether this credit can be converted to a manual refund. Not derivable from
	 * `invoiced`, which is also true for EFTPOS and CASH vouchers — those are convertible.
	 */
	canConvertToRefund?: boolean;
	/** The credit was paid back out as a refund, rather than voided outright. */
	refunded?: boolean;
	/** When it was paid back out — the linked refund's creation time. */
	refundedDate?: string;
}

export interface AddUpdateVoucherDto {
	userId?: string;
	voucherAmount: number;
	expiryDate: Date;
	voucherId?: string;
	price?: number;
	invoiced?: boolean;
	recipientEmail?: string;
	sendNotificationEmail?: boolean;
}
export interface GiftVouchersProps {
	reload: () => void;
	listOfGiftCertificates?: FetchedGiftVouchersDto;
}

interface editVoucherOptions {
	userId: string;
	refresh: () => void;
	voucherId: string;
	voucherAmount: number;
	amountRemaining: number;
	expiryDate: Date;
	recipientEmail?: string;
	userName?: string;
	hasUser?: boolean;
}

/**
 * Starts the user flow to edit a voucher. The flow can involve multiple pop-ups.
 */
export async function startUpdatingVoucher({
	userId,
	refresh,
	voucherId,
	voucherAmount,
	amountRemaining,
	expiryDate,
	recipientEmail,
	userName,
	hasUser,
}: editVoucherOptions) {
	const initialUserId = userId ?? undefined;
	const initialRecipientEmail = recipientEmail ?? '';
	const state = observable<GiftVoucherState>({
		voucherAmount: amountRemaining ?? 0,
		expiryDate: expiryDate ?? moment(new Date()).add(appConfigStore.creditVoucherExpiryYears, 'years').toDate(),
		recipientEmail: initialRecipientEmail,
		originalRecipientEmail: initialRecipientEmail,
		selectedUserId: initialUserId,
		originalUserId: initialUserId,
		customerName: userName ?? '',
		hasUser: hasUser ?? !!userId,
		sendNotificationEmail: false,
		editChoice: undefined,
	});

	const confirmed = await modalWithCustomInput(
		'Edit gift voucher',
		<GiftVoucherModalContent state={state} editMode />,
		{
			className: 'overflow',
			messageClassName: 'gift-voucher-edit-message',
			actionClassName: 'create-gift-certificate-modal',
			cancelText: 'Close',
			confirmText: 'Confirm update',
			resolveOnCancel: true,
		},
	);

	if (!confirmed) {
		return;
	}

	try {
		const updatingRecipient = !state.hasUser && state.editChoice === 'recipient';
		const updatingAccount = state.hasUser || state.editChoice === 'account';

		await updateGiftVoucher({
			voucherAmount: voucherAmount ?? 0,
			expiryDate: state.expiryDate,
			voucherId: voucherId,
			userId: updatingAccount ? (state.selectedUserId ?? userId) : userId || undefined,
			recipientEmail: updatingRecipient ? state.recipientEmail || undefined : undefined,
			sendNotificationEmail: updatingAccount ? state.sendNotificationEmail : false,
		});
		alertToast('Updated gift certificate', 'success');
		refresh();
	} catch (error: any) {
		// The endpoint answers 409 with a reason (e.g. the voucher has since been voided), so show it
		// rather than a generic failure the staff member can't act on.
		if (stringNotEmpty(error?.response?.data)) {
			alertToast(error.response.data, 'error');
			refresh();
		} else {
			alertToast('Unable to update gift certificate', 'error');
		}
	}
}

interface voidVoucherOptions {
	voucherId: string;
	code: string;
	refresh: () => void;
}

/**
 * Opens the confirm flow to void a voucher. Shared by the staff vouchers list and the customer's
 * credits tab so the two cannot drift — voiding is permanent, so the wording that warns about that
 * has to be the same wherever it is offered.
 */
export async function startVoidingVoucher({ voucherId, code, refresh }: voidVoucherOptions) {
	try {
		await newConfirmModal(
			'Void voucher',
			<>
				<p>
					Are you sure you want to void voucher <strong>#{code}</strong>?
				</p>
				<p>
					<strong>This action is permanent and cannot be reversed.</strong>
				</p>
				<p>Voiding confirms that the voucher value has been refunded outside of the system.</p>
			</>,
		);
	} catch {
		// User cancelled
		return;
	}

	try {
		await toggleVoucherDisabled(voucherId);
		alertToast('Voucher voided', 'success');
		refresh();
	} catch (error: any) {
		// The endpoint answers 400 with a reason (e.g. the voucher is already voided), so show it
		// rather than a generic failure the staff member can't act on.
		if (stringNotEmpty(error?.response?.data)) {
			alertToast(error.response.data, 'error');
			refresh();
		} else {
			alertToast('Unable to void voucher', 'error');
		}
	}
}

interface ConvertVoucherRefundNoteModel {
	note: string;
	noteError: string;
}

const ConvertVoucherRefundModalContent = observer(({
	code,
	remainingValue,
	noteModel,
}: {
	code: string;
	remainingValue: number;
	noteModel: ConvertVoucherRefundNoteModel;
}) => {
	return (
		<div className="manual-refund-modal-content">
			<MessageBox status="warning" slim>
				{'This voids the voucher and records a manual refund. It cannot be undone.'}
			</MessageBox>
			<p>
				Convert the remaining <strong>{formatCurrency(remainingValue)}</strong> on voucher{' '}
				<strong>#{code}</strong> into a manual refund? The voucher will be voided and a pending
				manual refund created for you to settle offline.
			</p>

			<div className="form-group">
				<TextArea
					id="convert-voucher-refund-note"
					label="Refund Note"
					model={noteModel}
					modelProperty="note"
					isRequired={appConfigStore.manualRefundNoteRequired}
					// Handed to the field rather than rendered as a loose div below it: InputWrapper
					// puts the message directly under the input and marks the group with
					// `input-group--error`. The old sibling div used `invalid-feedback`, which has no
					// stylesheet anywhere in the app, so it printed as plain text under the counter.
					errors={noteModel.noteError || undefined}
					placeholder="Please provide details about this manual refund (e.g., method used, reference number, etc.)"
					onAfterChange={e => {
						runInAction(() => {
							noteModel.note = e.target.value;
							if (noteModel.noteError) {
								noteModel.noteError = '';
							}
						});
					}}
				/>
				<div className="character-count">
					{noteModel.note.length}/500
				</div>
			</div>
		</div>
	);
});

interface convertVoucherToRefundOptions {
	voucherId: string;
	code: string;
	remainingValue: number;
	refresh: () => void;
}

/**
 * Opens the confirm flow to convert a credit voucher's remaining value into a manual (offline)
 * refund. Voids the voucher and creates a PENDING_MANUAL refund that staff then settle through the
 * booking's existing "Set to refunded" flow.
 */
export async function startConvertVoucherToManualRefund({
	voucherId,
	code,
	remainingValue,
	refresh,
}: convertVoucherToRefundOptions) {
	const noteModel = observable<ConvertVoucherRefundNoteModel>({
		note: '',
		noteError: '',
	});

	try {
		await newConfirmModal(
			'Convert to manual refund',
			<ConvertVoucherRefundModalContent code={code} remainingValue={remainingValue} noteModel={noteModel} />,
			{
				cancelText: 'Cancel',
				confirmText: 'Convert to refund',
				actionClassName: 'manual-refund-modal',
				resolveOnCancel: false,
				onBeforeConfirm: async () => {
					let valid = true;
					runInAction(() => {
						if (appConfigStore.manualRefundNoteRequired
							&& (!noteModel.note || noteModel.note.trim().length === 0)) {
							noteModel.noteError = 'Note is required';
							valid = false;
							return;
						}
						if (noteModel.note.trim().length > 500) {
							noteModel.noteError = 'Note must be less than 500 characters';
							valid = false;
							return;
						}
						noteModel.noteError = '';
					});
					if (!valid) {
						return false;
					}

					try {
						await convertVoucherToManualRefund(voucherId, noteModel.note.trim());
						alertToast('Voucher converted to a pending manual refund', 'success');
						refresh();
						return true;
					} catch (error: any) {
						if (stringNotEmpty(error?.response?.data)) {
							alertToast(error.response.data, 'error');
						} else {
							alertToast('Unable to convert voucher to a manual refund', 'error');
						}
						return false;
					}
				},
			},
		);
	} catch {
		// User cancelled
	}
}
